import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import styled from '@emotion/styled';

import { forMobile } from '../../responsive';
import { scrollbarStyles } from '../../scrollbar';
import { spacing } from '../../spacing';
import { IconButton } from '../Button';
import { ModalVariant } from './types';

const forwardProp = (prop: string) => !prop.startsWith('$');

export const StyledBackdrop = styled(BaseDialog.Backdrop)(({ theme }) => ({
  position: 'fixed',
  minHeight: '100dvh',
  inset: 0,
  backgroundColor: theme.colors.black[100],
  opacity: 0.5,
  transition: 'opacity 150ms cubic-bezier(0.45, 1.005, 0, 1.005)',
  touchAction: 'none',

  '&[data-starting-style], &[data-ending-style]': {
    opacity: 0,
  },
}));

export const StyledPopup = styled(BaseDialog.Popup, { shouldForwardProp: forwardProp })<{
  $maxWidth?: number;
  $maxHeight?: number | string;
  $gap?: number;
}>(({ theme, $maxWidth, $maxHeight, $gap }) => ({
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: $maxWidth ?? 704,
  maxHeight: $maxHeight ?? '80dvh',
  padding: spacing[24],
  gap: $gap ?? spacing[24],
  borderRadius: theme.radius[12],
  backgroundColor: theme.colors.background.raised,
  transition: 'opacity 150ms, transform 150ms',
  touchAction: 'none',

  '&[data-starting-style], &[data-ending-style]': {
    opacity: 0,
    transform: 'translate(-50%, -50%) scale(0.9)',
  },

  [forMobile]: {
    width: 'calc(100% - 24px)',
    maxWidth: 'none',
    maxHeight: $maxHeight ?? '90dvh',
    backgroundColor: theme.colors.background.raised,
  },
}));

export const Header = styled('div', { shouldForwardProp: forwardProp })<{
  $variant: ModalVariant;
}>(({ $variant }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing[8],

  ...($variant === 'center' && { alignItems: 'center' }),
}));

export const HeaderIcon = styled('div')(({ theme }) => ({
  width: 72,
  height: 72,
  display: 'grid',
  placeItems: 'center',
  alignSelf: 'center',
  flexShrink: 0,
  borderRadius: theme.radius.full,
  backgroundColor: theme.colors.background.overlay,
  color: theme.colors.brand.text,

  '& svg': { width: 32, height: 32 },
}));

export const TitleRow = styled('div')({
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  gap: spacing[8],
});

export const PopupContent = styled('div', { shouldForwardProp: forwardProp })<{
  $padding?: number | string;
}>(({ $padding }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  boxSizing: 'border-box',
  padding: $padding ?? spacing[4],
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  scrollbarGutter: 'stable both-edges',
  flex: 1,
  minHeight: 0,
  ...scrollbarStyles,
}));

export const Actions = styled('div', { shouldForwardProp: forwardProp })<{
  $variant: ModalVariant;
}>(({ $variant }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $variant === 'center' ? spacing[12] : spacing[24],
  justifyContent: $variant === 'center' ? 'center' : 'space-between',
}));

export const ActionGroup = styled('div', { shouldForwardProp: forwardProp })<{
  $variant: ModalVariant;
  $stacked: boolean;
}>(({ $variant, $stacked }) => ({
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  gap: spacing[12],
  justifyContent: $variant === 'center' ? 'center' : 'flex-end',

  ...($variant === 'center' && { '& > button': { flex: 1 } }),
  ...($stacked && {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: spacing[8],
    '& > button': { flex: 'none', width: '100%' },
  }),
}));

export const IconSpacer = styled('div')({
  flexShrink: 0,
  width: 24,
  height: 24,

  [forMobile]: {
    width: 44,
    height: 44,
    margin: -14,
  },
});

/**
 * The design system's `xs` IconButton is 32px across, below the 44px a finger
 * needs. Only the padding grows on a phone; the glyph stays the standard one.
 */
export const CloseButton = styled(IconButton)({
  [forMobile]: {
    '&&': { padding: spacing[16] },
  },
});
